import {
  ehrErrorMessage,
  type AppointmentResponse,
  type PatientResponse,
  type WaitlistEntry,
} from '@vagusx/clients';

/**
 * Where a record stands with the EHR, as the UI shows it (ADR-284). One shape
 * for patients, appointments and waitlist entries so all render through
 * `EhrSyncStatus`.
 *
 * `error` is already the staff message (the registry's text for a registered
 * code, else the stored text), never the raw upstream detail.
 */
export type EhrSyncState =
  | { kind: 'synced'; syncedAt: string | null }
  /** Being sent right now: someone asked and is watching it land. */
  | { kind: 'syncing' }
  /** Waiting for the push to pick it up; nothing says it is being sent yet (ADR-292). */
  | { kind: 'queued' }
  | { kind: 'retrying'; error: string | null; lastSyncedAt: string | null }
  | { kind: 'failed'; failedAt: string | null; error: string | null; lastSyncedAt: string | null }
  | { kind: 'never' };

/** EHR_SYNC_STATUS (apps/api/src/appointments/shared.ts). */
const APPOINTMENT_SYNC = { PENDING: 0, SYNCED: 1, FAILED: 2 } as const;

type PatientSyncFields = Pick<PatientResponse, 'ehrPatientId' | 'lastEhrSyncAt'> &
  Partial<Pick<PatientResponse, 'ehrSyncFailedAt' | 'ehrSyncError' | 'ehrSyncErrorCode'>>;

/**
 * A patient's EHR sync state, or null for a patient with no EHR chart (added
 * by hand, or the practice has no EHR), which has nothing to sync.
 */
export function patientEhrSyncState(patient: PatientSyncFields): EhrSyncState | null {
  if (!patient.ehrPatientId) return null;
  if (patient.ehrSyncFailedAt) {
    return {
      kind: 'failed',
      failedAt: patient.ehrSyncFailedAt,
      error: ehrErrorMessage(patient.ehrSyncErrorCode, patient.ehrSyncError),
      lastSyncedAt: patient.lastEhrSyncAt,
    };
  }
  if (patient.lastEhrSyncAt) return { kind: 'synced', syncedAt: patient.lastEhrSyncAt };
  return { kind: 'never' };
}

type AppointmentSyncFields = Pick<
  AppointmentResponse,
  'ehrSyncStatus' | 'ehrSyncError' | 'ehrSyncErrorCode' | 'lastEhrSyncAt'
>;

/**
 * An appointment's EHR sync state, or null when it has none to show: not
 * applicable (no EHR, or nothing owed to it) or a response without the field.
 *
 * A PENDING row with an error is one whose write failed and is waiting for its
 * next automatic retry; without an error it is queued and about to go.
 */
export function appointmentEhrSyncState(appointment: AppointmentSyncFields): EhrSyncState | null {
  const lastSyncedAt = appointment.lastEhrSyncAt ?? null;
  const error = ehrErrorMessage(appointment.ehrSyncErrorCode, appointment.ehrSyncError);
  switch (appointment.ehrSyncStatus) {
    case APPOINTMENT_SYNC.SYNCED:
      return { kind: 'synced', syncedAt: lastSyncedAt };
    case APPOINTMENT_SYNC.PENDING:
      return error ? { kind: 'retrying', error, lastSyncedAt } : { kind: 'syncing' };
    case APPOINTMENT_SYNC.FAILED:
      // The failure time isn't stored for appointments; `updatedAt` moves on
      // any edit, so it would be a guess.
      return { kind: 'failed', failedAt: null, error, lastSyncedAt };
    default:
      return null;
  }
}

/**
 * A waitlist entry's EHR sync state, or null when it isn't sent to an EHR (the
 * practice's EHR keeps no waitlist, or there is no EHR).
 *
 * As for appointments, a pending entry with an error failed its last push and
 * is waiting for the next automatic retry. A pending entry without one is
 * `queued`: the data can't say a worker has picked it up, so it doesn't claim
 * to be sending (ADR-292). `sending` is the one thing that can: the viewer
 * pressed _Send to EHR now_ and is watching the push settle.
 */
export function waitlistEhrSyncState(
  ehr: WaitlistEntry['ehr'],
  { sending = false }: { sending?: boolean } = {}
): EhrSyncState | null {
  const lastSyncedAt = ehr.lastSyncedAt;
  const error = ehrErrorMessage(ehr.errorCode, ehr.error);
  switch (ehr.syncStatus) {
    case 'synced':
      return { kind: 'synced', syncedAt: lastSyncedAt };
    case 'pending':
      if (error) return { kind: 'retrying', error, lastSyncedAt };
      return sending ? { kind: 'syncing' } : { kind: 'queued' };
    case 'failed':
      // No failure time is stored; `updatedAt` moves on any edit.
      return { kind: 'failed', failedAt: null, error, lastSyncedAt };
    case 'not_applicable':
      return null;
  }
}

/** Whether staff can ask for the sync to be tried again from this state. */
export function canResync(state: EhrSyncState | null): boolean {
  return state?.kind === 'failed' || state?.kind === 'retrying';
}

/** APPOINTMENT_STATUS.BOOKED (apps/api/src/appointments/scheduled/schema.ts). */
const APPOINTMENT_BOOKED = 2;

/**
 * Whether a re-sync of this appointment would BOOK it in the EHR: a BOOKED
 * appointment the EHR has no id for, the one case the reconcile worker books
 * (`actionFromState`). Worth a confirmation, because after a failed booking
 * staff are asked to book it by hand, and a retry on top of that books the
 * patient twice. A cancel or reschedule retry can't do that.
 */
export function resyncWouldBook(
  appointment: Pick<AppointmentResponse, 'status' | 'ehrAppointmentId'>
): boolean {
  return !appointment.ehrAppointmentId && appointment.status === APPOINTMENT_BOOKED;
}

/** How often to re-read appointments while any is queued for the EHR. */
const SYNCING_POLL_MS = 5_000;
/** The worker runs at least every 5 minutes; past this, polling won't see it land. */
const SYNCING_POLL_WINDOW_MS = 10 * 60_000;

/**
 * A `refetchInterval` that polls only while some record is queued for the EHR
 * with no error yet (`queued`), and was queued in the last 10 minutes: right
 * after a re-sync, until the worker reports back. A record waiting on a backoff
 * (it has an error) doesn't poll: its next try may be hours away.
 */
function queuedRefetchInterval(
  rows: ReadonlyArray<{ queued: boolean; updatedAt?: string | null }>,
  now: number
): number | false {
  const polling = rows.some(
    (r) =>
      r.queued &&
      r.updatedAt != null &&
      now - new Date(r.updatedAt).getTime() < SYNCING_POLL_WINDOW_MS
  );
  return polling ? SYNCING_POLL_MS : false;
}

/** `queuedRefetchInterval` for appointments. */
export function syncingRefetchInterval(
  appointments: ReadonlyArray<{
    ehrSyncStatus?: number;
    ehrSyncError?: string | null;
    updatedAt?: string | null;
  }>,
  now: number = Date.now()
): number | false {
  return queuedRefetchInterval(
    appointments.map((a) => ({
      queued: a.ehrSyncStatus === APPOINTMENT_SYNC.PENDING && !a.ehrSyncError,
      updatedAt: a.updatedAt,
    })),
    now
  );
}

/** Past the first 10 minutes, how often a still-queued waitlist entry is re-read. */
const QUEUED_SLOW_POLL_MS = 60_000;

/**
 * How often to re-read waitlist entries: every 5 s while one is queued and
 * became due in the last 10 minutes (the push runs within seconds of a change,
 * and at worst on the 5-minute tick), then every minute while one is still
 * queued, so a push that lands late still replaces "Queued" (ADR-292). Nothing
 * while every entry is settled or waiting on a backoff (its next try may be
 * hours away).
 *
 * The clock starts at `ehr.nextAttemptAt`, when the entry became due, not at
 * `updatedAt`: a push outcome doesn't move `updatedAt`, and a re-queue sets
 * both. "Queued" is exactly what `waitlistEhrSyncState` shows as queued or
 * syncing, so the two never disagree.
 */
export function waitlistSyncingRefetchInterval(
  entries: ReadonlyArray<Pick<WaitlistEntry, 'ehr' | 'updatedAt'>>,
  now: number = Date.now()
): number | false {
  const queued = entries.filter((e) => waitlistEhrSyncState(e.ehr)?.kind === 'queued');
  if (queued.length === 0) return false;
  const fast = queuedRefetchInterval(
    queued.map((e) => ({ queued: true, updatedAt: e.ehr.nextAttemptAt ?? e.updatedAt })),
    now
  );
  return fast === false ? QUEUED_SLOW_POLL_MS : fast;
}
